import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  DOCUMENT
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { firstValueFrom, map, Observable } from 'rxjs';
import { DialogComponent, DialogConfig } from '../components/dialog/dialog.component';
import { DrawerComponent } from '../components/drawer/drawer.component';

@Injectable({
  providedIn: 'root'
})
export class DialogDrawerService {
  private appRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);
  private document = inject(DOCUMENT);
  private breakpointObserver = inject(BreakpointObserver);

  private isMobile$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Handset])
    .pipe(map((result) => result.matches));

  async showUserPrompt(config: DialogConfig): Promise<string | undefined> {
    const isMobile = await firstValueFrom(this.isMobile$);

    if (isMobile) {
      return this.showDrawer(config);
    } else {
      return this.showDialog(config);
    }
  }

  private createComponent(
    config: DialogConfig,
    componentType: typeof DrawerComponent | typeof DialogComponent
  ): Promise<string | undefined> {
    return new Promise((resolve) => {
      const componentRef = createComponent(componentType, {
        environmentInjector: this.injector
      });

      componentRef.setInput('config', config);

      componentRef.instance.actionSelected.subscribe((result: string) => {
        this.destroyComponent(componentRef);
        resolve(result);
      });

      this.document.body.appendChild(componentRef.location.nativeElement);
      this.appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  private showDialog(config: DialogConfig) {
    return this.createComponent(config, DialogComponent);
  }

  private showDrawer(config: DialogConfig): Promise<string | undefined> {
    return this.createComponent(config, DrawerComponent);
  }

  private destroyComponent(componentRef: ComponentRef<any>) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }

  // Convenience methods for common dialogs
  async showErrorDialog(title: string, message: string): Promise<void> {
    await this.showUserPrompt({
      title,
      message,
      icon: 'error',
      iconColor: '#f44336',
      actions: [{ text: 'Ok', action: 'ok', color: 'primary', variant: 'raised' }]
    });
  }

  async showWarningDialog(title: string, message: string): Promise<boolean> {
    const result = await this.showUserPrompt({
      title,
      message,
      icon: 'warning',
      iconColor: '#ff9800',
      actions: [
        { text: 'Abbrechen', action: 'cancel', color: 'primary', variant: 'basic' },
        { text: 'Fortfahren', action: 'continue', color: 'warn', variant: 'raised' }
      ]
    });

    return result === 'continue';
  }
}
